import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Input } from '../../../components/ui/input';
import { Download, Search, CheckSquare, Square } from 'lucide-react';

const ExportDispatchesModal = ({ 
  isOpen, 
  onClose, 
  dispatches, 
  getImportadoraName,
  onExport 
}) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Filtrar despachos según búsqueda
  const filteredDispatches = useMemo(() => {
    if (!searchTerm) return dispatches;
    const term = searchTerm.toLowerCase();
    return dispatches.filter(d => 
      d.dispatch_number?.toLowerCase().includes(term) ||
      getImportadoraName(d.importadora_id)?.toLowerCase().includes(term) ||
      d.crt_number?.toLowerCase().includes(term)
    );
  }, [dispatches, searchTerm, getImportadoraName]);

  // Toggle selección individual
  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  // Seleccionar/Deseleccionar todos (filtrados)
  const toggleSelectAll = () => {
    const filteredIds = filteredDispatches.map(d => d.id);
    const allSelected = filteredIds.every(id => selectedIds.includes(id));
    
    if (allSelected) {
      setSelectedIds(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      setSelectedIds(prev => [...new Set([...prev, ...filteredIds])]);
    }
  };

  // Verificar si todos los filtrados están seleccionados
  const allFilteredSelected = useMemo(() => {
    const filteredIds = filteredDispatches.map(d => d.id);
    return filteredIds.length > 0 && filteredIds.every(id => selectedIds.includes(id));
  }, [filteredDispatches, selectedIds]);

  // Obtener despachos seleccionados
  const selectedDispatches = useMemo(() => {
    return dispatches.filter(d => selectedIds.includes(d.id));
  }, [dispatches, selectedIds]);

  // Manejar exportación
  const handleExport = () => {
    if (selectedDispatches.length === 0) return;
    onExport(selectedDispatches);
    onClose();
    setSelectedIds([]);
    setSearchTerm('');
  };

  // Limpiar al cerrar
  const handleClose = () => {
    onClose();
    setSelectedIds([]);
    setSearchTerm('');
  };

  // Obtener conteo de camiones
  const getTruckCount = (dispatch) => {
    return dispatch.trucks?.length || 0;
  };

  // Obtener estado con color
  const getStatusBadge = (status) => {
    const colors = {
      'Pendiente': 'bg-amber-100 text-amber-700',
      'En Proceso': 'bg-blue-100 text-blue-700',
      'Finalizado': 'bg-emerald-100 text-emerald-700'
    };
    return (
      <span className={`px-1.5 py-0.5 rounded text-xs ${colors[status] || 'bg-slate-100 text-slate-700'}`}>
        {status}
      </span>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-slate-800">
            Exportar Despachos
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 flex-1 overflow-hidden flex flex-col">
          {/* Barra de búsqueda y acciones */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Buscar por N° despacho, importador o CRT..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={toggleSelectAll}
              className="h-8 text-xs"
            >
              {allFilteredSelected ? (
                <><Square size={14} className="mr-1" /> Deseleccionar</>
              ) : (
                <><CheckSquare size={14} className="mr-1" /> Seleccionar todos</>
              )}
            </Button>
          </div>

          {/* Contador de selección */}
          <div className="flex items-center justify-between px-1">
            <span className="text-xs text-slate-500">
              {filteredDispatches.length} despachos encontrados
            </span>
            <span className="text-xs font-medium text-slate-700">
              {selectedIds.length} seleccionados
            </span>
          </div>

          {/* Lista de despachos */}
          <div className="flex-1 overflow-y-auto border border-slate-200 rounded-sm">
            {filteredDispatches.length === 0 ? (
              <div className="p-4 text-center text-slate-400 text-sm">
                No se encontraron despachos
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredDispatches.map((dispatch) => {
                  const isSelected = selectedIds.includes(dispatch.id);
                  return (
                    <div 
                      key={dispatch.id}
                      onClick={() => toggleSelect(dispatch.id)}
                      className={`flex items-center gap-3 p-2.5 cursor-pointer transition-colors ${
                        isSelected 
                          ? 'bg-blue-50 hover:bg-blue-100' 
                          : 'hover:bg-slate-50'
                      }`}
                    >
                      <Checkbox 
                        checked={isSelected}
                        onCheckedChange={() => toggleSelect(dispatch.id)}
                        className="pointer-events-none"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-medium text-sm text-slate-800">
                            {dispatch.dispatch_number}
                          </span>
                          {getStatusBadge(dispatch.status)}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500">
                          <span>{getImportadoraName(dispatch.importadora_id)}</span>
                          <span>•</span>
                          <span>CRT: {dispatch.crt_number}</span>
                          <span>•</span>
                          <span>{getTruckCount(dispatch)} camión(es)</span>
                        </div>
                      </div>
                      <div className="text-right text-xs text-slate-400">
                        <div>{dispatch.load_date}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer con botón de exportar */}
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <Button variant="outline" onClick={handleClose} size="sm">
              Cancelar
            </Button>
            <Button 
              onClick={handleExport}
              disabled={selectedIds.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              size="sm"
            >
              <Download size={14} className="mr-1" />
              Exportar {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportDispatchesModal;
